import React, { useEffect, useMemo, useRef, useState } from "react";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DragOverlay } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import Service from "../Service";
import type { tMasterService } from "@/types";
import type { TFunction } from "react-i18next";
import { MasterServicesService } from "@/services/masterServices";

type tProps = {
  services: tMasterService[];
  serviceBaseProps: { masterId: string; openEditor: tVoidFn } & tAnyDict;
  t: TFunction;
};
const DraggableServices = (props: tProps) => {
  const [draggableItem, setDraggableItem] =
    useState<Maybe<tMasterService>>(null);
  const [services, setServices] = useState(props.services);
  const renderDraggableItem = () => (
    <DraggableItem
      serviceBaseProps={props.serviceBaseProps}
      item={draggableItem}
      t={props.t}
    />
  );
  const servicesFirstRenderRef = useRef(true);
  useEffect(() => {
    if (servicesFirstRenderRef.current) {
      servicesFirstRenderRef.current = false;
    } else {
      setServices(props.services);
    }
  }, [props.services]);
  const servicesIds = useMemo(() => {
    return services.map((s) => s._id);
  }, [services]);
  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    const activeId = active?.id as string;
    const overId = over?.id as string;
    if (activeId && overId && activeId !== overId) {
      const srcIdx = services.findIndex((s) => s._id === activeId);
      const dstIdx = services.findIndex((s) => s._id === overId);
      if (srcIdx !== -1 && dstIdx !== -1) {
        const initialServices = services;
        const optimisticallyRearranged = arrayMove(services, srcIdx, dstIdx);
        setServices(optimisticallyRearranged);
        try {
          await MasterServicesService.changeServicePosition(activeId, {
            insert_id: overId,
          });
        } catch (e) {
          setServices(initialServices);
          console.error(e);
        }
      }
    }
    setDraggableItem(null);
  };
  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={({ active }) => {
        setDraggableItem(services.find((t) => t._id === active.id) || null);
      }}
      onDragEnd={onDragEnd}
      onDragCancel={() => setDraggableItem(null)}
    >
      <SortableContext
        items={servicesIds}
        strategy={verticalListSortingStrategy}
      >
        {services.map((service) => (
          <SortableRow
            key={service._id}
            serviceBaseProps={props.serviceBaseProps}
            service={service}
            t={props.t}
          />
        ))}
      </SortableContext>
      {renderDraggableItem()}
    </DndContext>
  );
};

type tSortableRowProps = {
  service: tMasterService;
  serviceBaseProps: tProps["serviceBaseProps"];
  t: TFunction;
};
const SortableRow = (props: tSortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
    transform,
    transition,
    setActivatorNodeRef,
  } = useSortable({ id: props.service._id });
  return (
    <div
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
      }}
      ref={setNodeRef}
      {...attributes}
    >
      <Service
        t={props.t}
        {...props.serviceBaseProps}
        service={props.service}
        isDraggable
        dragHandleRef={setActivatorNodeRef}
        dragHandleListeners={listeners}
      />
    </div>
  );
};

type tDraggableItemProps = {
  item: Maybe<tMasterService>;
  serviceBaseProps: tProps["serviceBaseProps"];
  t: TFunction;
};
const DraggableItem = ({
  item: draggableItem,
  serviceBaseProps,
  t,
}: tDraggableItemProps) => {
  const render = () => {
    if (!draggableItem) return null;
    return (
      <Service
        t={t}
        service={draggableItem}
        {...serviceBaseProps}
        isDraggable
      />
    );
  };
  return <DragOverlay>{render()}</DragOverlay>;
};

export default DraggableServices;
